
import Vue, { PropType } from 'vue';
import { Modal, Icon, Tab, Tabs, Button, Toast } from '@descomplica/vue-components';
import PostGradTCCAssessmentModalSkeleton from '@/components/skeletons/PostGradTCCAssessmentModalSkeleton.vue';
import PostGradFinalTCCTab from '@/components/modals/assessments/tccComponents/PostGradFinalTCCTab.vue';
import {
  Assessment,
  AssessmentResult,
  ASSESSMENT_RESULT_STATUS,
  REPLY_STATUS,
  REPLY_TYPE,
} from '@/models/greathall/assessment.model';
import i18n from '@/i18n';
import PostGradTCCThemeTab from './tccComponents/PostGradTCCThemeTab.vue';
import PostGradTCCOrientations from './tccComponents/PostGradTCCOrientations.vue';
import PostGradTCCDevelopmentTab from './tccComponents/PostGradTCCDevelopmentTab.vue';
import PostGradTCCEvaluationCriteriaTab from './tccComponents/PostGradTCCEvaluationCriteriaTab.vue';
import PostGradTCCDialogModal from './PostGradTCCDialogModal.vue';

import { TabType } from './tccComponents/types';

interface Data {
  isActive: boolean;
  isLoading: boolean;
  showGoToTop: boolean;
  showToast: boolean;
  toastMessage: string;
  toastIsError: boolean;
  currentTab: TabType | null;
  dialog?: any;
}

export default Vue.extend({
  components: {
    Modal,
    Tabs,
    Tab,
    Icon,
    TextButton: Button,
    PostGradFinalTCCTab,
    Toast,
    PostGradTCCAssessmentModalSkeleton,
    PostGradTCCThemeTab,
    PostGradTCCOrientations,
    PostGradTCCDevelopmentTab,
    PostGradTCCEvaluationCriteriaTab,
    PostGradTCCDialogModal,
  },
  props: {
    active: {
      type: Boolean,
      default: false,
    },
    assessment: {
      type: Object as PropType<Assessment>,
      required: true,
    },
    loading: {
      type: Boolean,
      default: true,
    },
  },
  data(): Data {
    return {
      isActive: this.active,
      isLoading: this.loading,
      currentTab: null,
      showToast: false,
      toastMessage: '',
      toastIsError: false,
      showGoToTop: false,
      dialog: null,
    };
  },
  computed: {
    borderRadius(): number {
      return this.getBorderRadius(window.innerWidth);
    },
    isThesisDevelopmentTabAvailable(): boolean {
      return this.isTabAvailable([
        ASSESSMENT_RESULT_STATUS.TCC_THEME_APPROVED,
        ASSESSMENT_RESULT_STATUS.PARTIAL_TCC_PENDING_REVIEW,
        ASSESSMENT_RESULT_STATUS.PARTIAL_TCC_APPROVED,
        ASSESSMENT_RESULT_STATUS.PARTIAL_TCC_REJECTED,
        ASSESSMENT_RESULT_STATUS.FAILED_ELEGIBLE_FOR_REVALUATION,
        ASSESSMENT_RESULT_STATUS.WAITING_GRADING,
        ASSESSMENT_RESULT_STATUS.PASSED,
        ASSESSMENT_RESULT_STATUS.FAILED,
      ]);
    },
    isFinalThesisTabAvailable(): boolean {
      if (!this.assessmentResult) return false;

      const partialTCCReplies = this.assessmentResult.replies.filter(
        (reply) => reply.type === REPLY_TYPE.PARTIAL_TCC,
      );
      const somePartialApproved = partialTCCReplies.some(
        (reply) => reply.status === REPLY_STATUS.APPROVED,
      );
      const reachedMaxPartialSubmissions =
        partialTCCReplies.length === this.assessmentResult.maxPartialTCCSubmissions;

      return (
        this.isTabAvailable([
          ASSESSMENT_RESULT_STATUS.FAILED_ELEGIBLE_FOR_REVALUATION,
          ASSESSMENT_RESULT_STATUS.WAITING_REVALUATION,
          ASSESSMENT_RESULT_STATUS.PARTIAL_TCC_APPROVED,
          ASSESSMENT_RESULT_STATUS.PARTIAL_TCC_REJECTED,
          ASSESSMENT_RESULT_STATUS.WAITING_GRADING,
          ASSESSMENT_RESULT_STATUS.PASSED,
          ASSESSMENT_RESULT_STATUS.FAILED,
        ]) &&
        (somePartialApproved || reachedMaxPartialSubmissions)
      );
    },
    dexCourseId(): number {
      return this.$store.getters.currentCourse.id;
    },
    assessmentResult(): AssessmentResult {
      return this.assessment?.assessmentResultsByUser[0];
    },
  },
  watch: {
    active(value) {
      this.isActive = value;
    },
    loading(value) {
      this.isLoading = value;
    },
    isLoading(value) {
      if (value === true) return;
      if (this.currentTab) {
        this.$nextTick(this.scrollToBottom);
      } else {
        this.selectInitialTab();
      }
    },
    currentTab(tabName) {
      if (![TabType.orientations, TabType.evaluationCriteria].includes(tabName))
        this.$nextTick(this.scrollToBottom);
    },
  },
  methods: {
    updateActive(active: boolean) {
      this.$emit('update:active', active);
    },
    updateShowGoToTop(position: any) {
      this.showGoToTop =
        position.top > 100 &&
        ![TabType.orientations, TabType.evaluationCriteria].includes(this.currentTab as TabType);
    },
    onCloseClick() {
      this.updateActive(false);
    },
    getBorderRadius(width: number): number {
      return width > 495 ? 24 : 0;
    },
    isTabAvailable(availableStatus: Array<ASSESSMENT_RESULT_STATUS>) {
      return this.assessmentResult && availableStatus.includes(this.assessmentResult.status);
    },
    isSelected(tabName: string): boolean {
      return this.currentTab?.toString() === tabName;
    },
    setIsLoading(value: boolean) {
      this.isLoading = value;
    },
    scrollToBottom() {
      const anchor = this.$refs.bottomAnchor;
      // @ts-ignore
      // istanbul ignore next
      if (anchor) anchor.scrollIntoView();
    },
    scrollToTop() {
      const anchor = this.$refs.topAnchor;
      // @ts-ignore
      // istanbul ignore next
      if (anchor) anchor.scrollIntoView({ behavior: 'smooth' });
    },
    onTabSelected(tabName: string) {
      const tabNameToType = {
        [i18n.t('yourAssessments.tcc.tabs.orientationsItems.title') as string]:
          TabType.orientations,
        [i18n.t('yourAssessments.tcc.tabs.evaluationCriteria.title') as string]:
          TabType.evaluationCriteria,
        [i18n.t('yourAssessments.tcc.tabs.thesisTheme.title') as string]: TabType.thesisTheme,
        [i18n.t('yourAssessments.tcc.tabs.thesisDevelopment.title') as string]:
          TabType.thesisDevelopment,
        [i18n.t('yourAssessments.tcc.tabs.finalThesis.label') as string]: TabType.finalThesis,
      };

      this.currentTab = tabNameToType[tabName];
    },
    selectInitialTab() {
      let initialTab = !this.assessmentResult ? TabType.orientations : TabType.thesisTheme;
      if (this.assessmentResult && this.assessmentResult.lastReply) {
        const { lastReply } = this.assessmentResult;

        if (lastReply.type === REPLY_TYPE.TCC_THEME) {
          initialTab = TabType.thesisTheme;
        }

        if (this.isThesisDevelopmentTabAvailable && lastReply.type === REPLY_TYPE.PARTIAL_TCC) {
          initialTab = TabType.thesisDevelopment;
        }

        if (
          this.isFinalThesisTabAvailable &&
          [REPLY_TYPE.FINAL_TCC, REPLY_TYPE.TCC_REVALUATION].includes(lastReply.type)
        ) {
          initialTab = TabType.finalThesis;
        }
      }

      this.currentTab = initialTab;
    },
    selectTab(tab: TabType) {
      this.currentTab = tab;
    },
    goToResetedView() {
      this.currentTab = TabType.thesisTheme;
      this.updateShowGoToTop({ top: 0 });
    },
    refreshCurrentView() {
      this.scrollToBottom();
    },
    displayToast(type: 'error' | 'success', message: string) {
      this.toastMessage = message;
      this.toastIsError = type === 'error';
      this.showToast = true;

      setTimeout(() => {
        this.showToast = false;
        this.toastIsError = false;
        this.toastMessage = '';
      }, 3000);
    },
    startDialog(dialogProps: any) {
      this.isActive = false;
      this.dialog = dialogProps;
    },
    finishDialog({ action }: { action: string }) {
      this.isActive = true;
      this.dialog = null;

      this.$nextTick(() => {
        // @ts-ignore
        this.$refs.finalTCCTab.finishRetryDialog({ action });
      });
    },
  },
});
